import { useState, useEffect, useCallback } from 'react';
import { api } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import {
  getCachedAuthenticatedResource,
  getOrFetchAuthenticatedResource,
  subscribeAuthenticatedResource,
} from '@/lib/authenticatedResource';

export interface PlanBucketStatus {
  plan_type?: string;
  billing_type?: string;
  limit?: number;
  usage?: number;
  remaining?: number;
  expires_at?: string | null;
  activated_at?: string | null;
  period_expired?: boolean;
  status?: string;
  usable?: boolean;
  balance_amount?: number;
  price_per_message?: number;
}

export interface PlanStatus {
  plan: string;
  billing_cycle_index: number;
  billing_cycle_start: string;
  billing_cycle_end: string;
  limit: number | null;
  usage: number;
  remaining: number | null;
  is_blocked: boolean;
  trial_expired: boolean;
  subscription_expired: boolean;
  plan_changed_at: string | null;
  active_bucket?: 'subscription' | 'prepaid' | null;
  subscription?: PlanBucketStatus | null;
  prepaid?: PlanBucketStatus | null;
  subscription_periods?: Array<{
    plan: string;
    plan_display: string;
    start_date: string;
    end_date: string;
    limit: number | null;
    usage: number;
    remaining: number | null;
  }>;
  /** Free trial (active or expired) only. trial_days null = legacy 1-month trial. */
  trial_offer?: string | null;
  trial_days?: number | null;
  trial_ends_at?: string;
}

const RESOURCE_KEY = 'plan-status' as const;
const DAY_MS = 24 * 60 * 60 * 1000;

/** Backend datetimes are naive UTC ISO strings. */
export function parseUtcDate(value: string): Date {
  return new Date(/[zZ]|[+-]\d{2}:\d{2}$/.test(value) ? value : `${value}Z`);
}

export function trialDurationLabel(days?: number | null): string {
  if (days == null) return '1 mes';
  return days === 1 ? '1 día' : `${days} días`;
}

export function trialExpiredMessage(status: Pick<PlanStatus, 'trial_days'>): string {
  return `Tu periodo de prueba de ${trialDurationLabel(status.trial_days)} terminó. Actualiza tu plan para seguir enviando mensajes.`;
}

/** Whole days left in an active free trial, or null when the plan is not a trial. */
export function trialDaysLeft(status: PlanStatus, now: Date = new Date()): number | null {
  if (status.plan !== 'free_trial' || !status.trial_ends_at) return null;
  const msLeft = parseUtcDate(status.trial_ends_at).getTime() - now.getTime();
  return Math.max(0, Math.ceil(msLeft / DAY_MS));
}

async function fetchPlanStatus(): Promise<PlanStatus> {
  const response = await api.get('/auth/me/plan-status');
  return response.data as PlanStatus;
}

export const usePlanStatus = () => {
  const [planStatus, setPlanStatus] = useState<PlanStatus | null>(
    () => getCachedAuthenticatedResource<PlanStatus>(RESOURCE_KEY) ?? null
  );
  const [loading, setLoading] = useState(
    () => !getCachedAuthenticatedResource<PlanStatus>(RESOURCE_KEY)
  );
  const [error, setError] = useState<string | null>(null);

  const loadPlanStatus = useCallback(async (
    options: boolean | { force?: boolean; isRetry?: boolean } = false
  ) => {
    const isRetry =
      options === true ||
      (typeof options === 'object' && !!options.isRetry);
    const force =
      (typeof options === 'object' && !!options.force) || isRetry;

    try {
      if (!isRetry) setLoading(true);
      setError(null);
      const data = await getOrFetchAuthenticatedResource(
        RESOURCE_KEY,
        fetchPlanStatus,
        { force }
      );
      setPlanStatus(data);
    } catch (err: any) {
      console.error('Error loading plan status (isRetry:', isRetry, '):', err);

      if (!isRetry) {
        console.log('🔄 Attempting automatic retry for plan status in 1.5s...');
        setTimeout(() => loadPlanStatus({ isRetry: true, force: true }), 1500);
        return;
      }

      setError(err.response?.data?.detail || 'Error al cargar el estado del plan');
      toast.error('Error al cargar el estado del plan');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const unsubscribe = subscribeAuthenticatedResource<PlanStatus>(
      RESOURCE_KEY,
      (data) => {
        if (data !== undefined) setPlanStatus(data);
      }
    );
    loadPlanStatus();
    return unsubscribe;
  }, [loadPlanStatus]);

  const getPlanDisplayName = (plan: string): string => {
    const planNames: Record<string, string> = {
      'free_trial': 'Free Trial',
      'basic_200': 'Básico',
      'standard_1000': 'Estándar',
      'premium_3000': 'Premium',
      'unlimited': 'Ilimitado',
      'blocked': 'Bloqueado',
      'test_5': '🧪 Pruebas',
      'prepaid': 'Prepago'
    };
    return planNames[plan] || plan;
  };

  const canSendMessages = (messageCount: number = 1): { allowed: boolean; reason?: string } => {
    if (!planStatus) {
      return { allowed: false, reason: 'Cargando estado del plan...' };
    }

    if (planStatus.is_blocked) {
      if (planStatus.trial_expired) {
        return { allowed: false, reason: trialExpiredMessage(planStatus) };
      }
      if (planStatus.subscription_expired) {
        return {
          allowed: false,
          reason: 'Tu suscripción ha expirado. Renueva tu plan para continuar enviando mensajes.'
        };
      }
      const limitText = planStatus.limit ? ` de ${planStatus.limit} mensajes` : '';
      return {
        allowed: false,
        reason: `Has alcanzado el límite${limitText} de mensajes entregados de tu plan ${getPlanDisplayName(planStatus.plan)} para este mes.`
      };
    }

    if (planStatus.plan === 'unlimited') {
      return { allowed: true };
    }

    if (planStatus.remaining !== null && planStatus.remaining < messageCount) {
      return {
        allowed: false,
        reason: `No tienes suficientes mensajes. Disponibles: ${planStatus.remaining}, Requeridos: ${messageCount}.`
      };
    }

    return { allowed: true };
  };

  const getBlockedMessage = (): string => {
    if (!planStatus || !planStatus.is_blocked) return '';

    if (planStatus.trial_expired) {
      return trialExpiredMessage(planStatus);
    }

    if (planStatus.subscription_expired) {
      return 'Tu suscripción ha expirado. Contacta al administrador para renovar tu plan.';
    }

    const limitText = planStatus.limit ? ` de ${planStatus.limit} mensajes` : '';
    return `Has alcanzado el límite${limitText} de mensajes entregados de tu plan ${getPlanDisplayName(planStatus.plan)} para este mes.`;
  };

  return {
    planStatus,
    loading,
    error,
    loadPlanStatus,
    getPlanDisplayName,
    canSendMessages,
    getBlockedMessage,
    isBlocked: planStatus?.is_blocked || false,
    isUnlimited: planStatus?.plan === 'unlimited',
    remainingMessages: planStatus?.remaining,
    usedMessages: planStatus?.usage || 0,
    totalMessages: planStatus?.limit
  };
};
